/** Painel inicial (17 §3.3): alertas abertos, próximas sessões e o que vale hoje para precificar. */
import { Title, useGetList, useGetIdentity } from "react-admin";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import { formatarData, formatarPercentual } from "../lib/formato";
import { AlertasAbertos, ProximasSessoes } from "../recursos/alertas";

interface ParametroVigente {
  id: string;
  vigente_desde: string;
  margem: number;
  seguro: number;
  custo_fixo: number;
  simples: number;
  frete_cif_pct: number;
}

interface RegraVigente {
  id: string;
  regra: string;
  vigente_desde: string;
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {rotulo}
      </Typography>
      <Typography variant="body2" fontWeight={700}>
        {valor}
      </Typography>
    </Stack>
  );
}

export function Painel() {
  const { identity } = useGetIdentity();
  const parametros = useGetList<ParametroVigente>("v_parametro_preco_vigente", {
    pagination: { page: 1, perPage: 1 },
    sort: { field: "vigente_desde", order: "DESC" },
  });
  const regra = useGetList<RegraVigente>("v_regra_preco_vigente", {
    pagination: { page: 1, perPage: 1 },
    sort: { field: "vigente_desde", order: "DESC" },
  });
  const p = parametros.data?.[0];
  const r = regra.data?.[0];
  const erro = parametros.error ?? regra.error;

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Title title="Painel" />
      <Typography variant="h5">Olá{identity?.fullName ? `, ${identity.fullName}` : ""}</Typography>
      {erro instanceof Error && <Alert severity="error">{erro.message}</Alert>}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <AlertasAbertos />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ProximasSessoes />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Padrões de preço em vigor
              </Typography>
              {p ? (
                <>
                  <Linha rotulo="Margem" valor={formatarPercentual(p.margem)} />
                  <Linha rotulo="Seguro" valor={formatarPercentual(p.seguro)} />
                  <Linha rotulo="Custo fixo" valor={formatarPercentual(p.custo_fixo)} />
                  <Linha rotulo="Simples" valor={formatarPercentual(p.simples)} />
                  <Linha rotulo="Frete CIF" valor={formatarPercentual(p.frete_cif_pct)} />
                  <Typography variant="caption" color="text.secondary">
                    Vigente desde {formatarData(p.vigente_desde)}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {parametros.isPending ? "Carregando…" : "Nenhum padrão vigente."}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Motor de preço
              </Typography>
              {r ? (
                <>
                  <Linha rotulo="Regra em vigor" valor={r.regra} />
                  <Typography variant="caption" color="text.secondary">
                    Vigente desde {formatarData(r.vigente_desde)}.
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {regra.isPending ? "Carregando…" : "Nenhuma regra vigente."}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
